import Modal from '@lyra/core/components/Modal'
import Section from '@lyra/core/components/Section'
import { TRADE_RIGHT_RAIL_WIDTH_DESKTOP } from '@lyra/web/constants/layout'
import { OrderQuoteParams } from '@lyra/web/constants/order'
import { LegsUnpriced, SendRfqParams } from '@lyra/web/constants/rfqs'
import useOrderParamsForInstrument from '@lyra/web/hooks/useOrderParamsForInstrument'
import useRfqParams from '@lyra/web/hooks/useRfqParams'
import useStrategy from '@lyra/web/hooks/useStrategy'
import useTickers from '@lyra/web/hooks/useTickers'
import { formatInstrumentWithDirection } from '@lyra/web/utils/instruments'
import { formatStrategyName } from '@lyra/web/utils/strategies'
import { useMemo, useState } from 'react'

import RfqTradeForm from './RfqTradeForm'
import TradeForm from './TradeForm'
import TradeFormOrderTypeTabs from './TradeFormOrderTypeTabs'

type Props = {
  order: SendRfqParams | OrderQuoteParams
  onClose: () => void
  isRfq?: boolean
  onSubmitOrder?: () => void
  title?: string
  subtitle?: string
  hideAdvancedSettings?: boolean
  disableTriggers?: boolean
  hideBuySellToggle?: boolean
  hideReceipt?: boolean
  hideOrderTypeToggle?: boolean
  showMarkPrice?: boolean
}

export default function TradeFormModal({
  order,
  onClose,
  title,
  subtitle,
  isRfq: initialIsRfq,
  onSubmitOrder,
  hideAdvancedSettings,
  hideBuySellToggle,
  hideReceipt,
  disableTriggers,
  hideOrderTypeToggle,
  showMarkPrice,
}: Props) {
  const legs: LegsUnpriced = useMemo(
    () =>
      'legs' in order
        ? order.legs
        : [
            {
              instrument_name: order.instrument_name,
              amount: order.amount,
              direction: order.direction,
            },
          ],
    [order]
  )
  const firstLeg = legs[0]

  const [isRfq, setIsRfq] = useState(initialIsRfq)
  const [rfqParams, setRfqParams] = useRfqParams({ legs })
  const [orderParams, setOrderParams] = useOrderParamsForInstrument(
    firstLeg.instrument_name,
    'instrument_name' in order ? order : undefined
  )

  const tickers = useTickers(legs.map((leg) => leg.instrument_name))

  const strategy = useStrategy(tickers, legs)

  const strategyName =
    legs.length === 1
      ? formatInstrumentWithDirection(legs[0].instrument_name, legs[0].direction === 'buy', {
          showExpiry: true,
        })
      : formatStrategyName(strategy)

  return (
    <Modal
      title={title ? title : strategyName}
      subtitle={subtitle}
      onClose={onClose}
      desktopWidth={TRADE_RIGHT_RAIL_WIDTH_DESKTOP}
    >
      {!hideOrderTypeToggle ? (
        <TradeFormOrderTypeTabs
          isRfq={isRfq}
          instruments={legs}
          orderParams={orderParams}
          setOrderParams={(params) => {
            setOrderParams(params)
            setIsRfq(false)
          }}
          rfqParams={rfqParams}
          setRfqParams={(params) => {
            setRfqParams(params)
            setIsRfq(true)
          }}
          disableTriggers={disableTriggers}
        />
      ) : null}
      {tickers ? (
        isRfq ? (
          <RfqTradeForm
            // TODO: support filtering for RFQ form
            rfqParams={rfqParams}
            setRfqParams={setRfqParams}
            onSubmitOrder={onSubmitOrder}
            hideReceipt={hideReceipt}
          />
        ) : (
          <TradeForm
            ticker={Object.values(tickers)[0]}
            orderParams={orderParams}
            setOrderParams={setOrderParams}
            onSubmitOrder={onSubmitOrder}
            hideAdvancedSettings={hideAdvancedSettings}
            hideBuySellToggle={hideBuySellToggle}
            hideReceipt={hideReceipt}
            showMarkPrice={showMarkPrice}
          />
        )
      ) : (
        <Section.Spinner />
      )}
    </Modal>
  )
}
